import React from 'react';
import { Article } from '../../config/api';
import { useArticleTracking } from '../../hooks/useArticles';
import { API_CONFIG } from '../../config/api';
import './ArticleSummary.css';

export interface ArticleSummaryProps {
  article: Article;
  showSource?: boolean;
  showTags?: boolean;
  showImage?: boolean;
  showShareButtons?: boolean;
  isHighlighted?: boolean;
  className?: string;
  shareUrl?: string; // Custom URL to share instead of article.source_url
}

const ArticleSummary: React.FC<ArticleSummaryProps> = ({
  article,
  showSource = true,
  showTags = false,
  showImage = true,
  showShareButtons = true,
  isHighlighted = false,
  className = '',
  shareUrl, // Custom URL to share
}) => {
  const { trackClick, trackShare } = useArticleTracking();

  const handleArticleClick = (e: React.MouseEvent) => {
    // Don't trigger if clicking on share buttons
    if ((e.target as HTMLElement).closest('.article-summary__share')) {
      return;
    }
    
    trackClick(article.slug);
    window.open(article.source_url, '_blank', 'noopener,noreferrer');
  };

  const handleShare = (platform: string) => {
    trackShare(article.slug, platform);
    
    // Use custom shareUrl if provided, otherwise use article detail page URL
    const baseUrl = shareUrl || `${window.location.origin}/article/${article.slug}`;
    
    // Add UTM parameters for tracking
    const utmParams = new URLSearchParams({
      utm_source: platform,
      utm_medium: 'social',
      utm_campaign: 'article_share'
    });
    
    const urlToShare = `${baseUrl}?${utmParams.toString()}`;
    const url = encodeURIComponent(urlToShare);
    const title = encodeURIComponent(article.title);
    
    let shareUrlFinal = '';
    
    switch (platform) {
      case 'x':
        shareUrlFinal = `https://twitter.com/intent/tweet?url=${url}&text=${title}`;
        break;
      case 'instagram':
        // Instagram doesn't have direct URL sharing, so we'll copy to clipboard
        navigator.clipboard.writeText(urlToShare);
        alert('Link copied! You can now paste it in your Instagram story or bio.');
        return;
      case 'facebook':
        shareUrlFinal = `https://www.facebook.com/sharer/sharer.php?u=${url}`;
        break;
      case 'linkedin':
        shareUrlFinal = `https://www.linkedin.com/sharing/share-offsite/?url=${url}`;
        break;
      case 'copy':
        navigator.clipboard.writeText(urlToShare);
        return;
      default:
        return;
    }
    
    if (shareUrlFinal) {
      window.open(shareUrlFinal, '_blank', 'width=600,height=400');
    }
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  // Use brief_summary if available, otherwise fall back to summary
  const displaySummary = article.brief_summary || article.summary;

  return (
    <article 
      className={`article-summary ${isHighlighted ? 'article-summary--highlighted' : ''} ${className}`}
      onClick={handleArticleClick}
    >
      {/* Image */}
      {showImage && article.social_image_url && (
        <div className="article-summary__image">
          <img
            src={`${API_CONFIG.BASE_URL}${article.social_image_url}`}
            alt={article.title}
            loading="lazy"
            onError={(e) => {
              // Hide image if it fails to load
              const target = e.target as HTMLImageElement;
              target.parentElement?.remove();
            }}
          />
        </div>
      )}

      {/* Content */}
      <div className="article-summary__content">
        {/* Header */}
        <header className="article-summary__header">
          <h3 className="article-summary__title">
            {article.title}
            <svg
              className="article-summary__external-icon"
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
              <polyline points="15,3 21,3 21,9" />
              <line x1="10" y1="14" x2="21" y2="3" />
            </svg>
          </h3>

          {showSource && (
            <div className="article-summary__meta">
              <span className="article-summary__source">
                {article.source_name}
              </span>
              {article.author && (
                <>
                  <span className="article-summary__separator">•</span>
                  <span className="article-summary__author">
                    {article.author}
                  </span>
                </>
              )}
              <span className="article-summary__separator">•</span>
              <time 
                className="article-summary__time"
                dateTime={article.published_at}
                title={new Date(article.published_at).toLocaleString()}
              >
                {formatTime(article.published_at)}
              </time>
            </div>
          )}
        </header>

        {/* Summary */}
        <div className="article-summary__summary">
          <p className="article-summary__text">
            {displaySummary}
          </p>
        </div>

        {/* Tags */}
        {(() => {
          let tags: string[] = [];
          try {
            if (typeof article.tags === 'string') {
              const parsed = JSON.parse(article.tags || '[]');
              tags = Array.isArray(parsed) ? parsed : [];
            } else if (Array.isArray(article.tags)) {
              tags = article.tags;
            }
          } catch (e) {
            tags = [];
          }
          
          return showTags && tags && tags.length > 0 && (
            <div className="article-summary__tags">
              {tags.slice(0, 3).map((tag: string, index: number) => (
                <span key={index} className="article-summary__tag">
                  {tag}
                </span>
              ))}
              {tags.length > 3 && (
                <span className="article-summary__tag article-summary__tag--more">
                  +{tags.length - 3}
                </span>
              )}
            </div>
          );
        })()}

        {/* Footer */}
        <footer className="article-summary__footer">
          <div className="article-summary__stats">
            {article.view_count > 0 && (
              <span className="article-summary__stat" title="Views">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                  <circle cx="12" cy="12" r="3" />
                </svg>
                {article.view_count}
              </span>
            )}
            
            {article.share_count > 0 && (
              <span className="article-summary__stat" title="Shares">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="18" cy="5" r="3" />
                  <circle cx="6" cy="12" r="3" />
                  <circle cx="18" cy="19" r="3" />
                  <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
                  <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
                </svg>
                {article.share_count}
              </span>
            )}

            <div className="article-summary__click-hint">
              Click to read full article
            </div>
          </div>

          {/* Share Buttons */}
          {showShareButtons && (
            <div className="article-summary__share">
              <button
                className="article-summary__share-button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleShare('x');
                }}
                title="Share on X (formerly Twitter)"
                aria-label="Share on X"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                </svg>
              </button>

              <button
                className="article-summary__share-button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleShare('instagram');
                }}
                title="Share on Instagram"
                aria-label="Share on Instagram"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                </svg>
              </button>

              <button
                className="article-summary__share-button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleShare('facebook');
                }}
                title="Share on Facebook"
                aria-label="Share on Facebook"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                </svg>
              </button>

              <button
                className="article-summary__share-button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleShare('linkedin');
                }}
                title="Share on LinkedIn"
                aria-label="Share on LinkedIn"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                </svg>
              </button>

              <button
                className="article-summary__share-button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleShare('copy');
                }}
                title="Copy link"
                aria-label="Copy link"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                  <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
                </svg>
              </button>
            </div>
          )}
        </footer>
      </div>
    </article>
  );
};

export default ArticleSummary;
